import { PayloadAction, SerializedError, createSlice } from '@reduxjs/toolkit';

import { bannersApiUrlCreator } from '@lib/core/banners/slices/urls';
import { TBanner, TGetBannersParams } from '@lib/core/banners/types';
import { createTypedAsyncThunk } from '@lib/core/service/createTypedAsyncThunk';
import { request } from '@lib/core/service/requests/request';

interface IState {
  data: TBanner[];
  discountBanner: TBanner | null;
  isDiscountVoucherShown: boolean;
  error: SerializedError;
  isLoading: boolean;
}

const initialState: IState = {
  data: [],
  discountBanner: null,
  error: null,
  isDiscountVoucherShown: false,
  isLoading: false,
};

export const actionGetBanners = createTypedAsyncThunk<TBanner[], TGetBannersParams>(
  'actionGetBanners',
  async params => {
    const fetchBanners = async (url?: string): Promise<TBanner[]> => {
      const { results = [], next } = await request({
        ...(url ? {} : { params }),
        url: url || bannersApiUrlCreator(),
      });

      if (!next) return results;

      return [...results, ...(await fetchBanners(next))];
    };

    return fetchBanners();
  },
);

export const bannersSlice = createSlice({
  extraReducers: builder => {
    builder.addCase(actionGetBanners.pending, state => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(actionGetBanners.fulfilled, (state, { payload }) => {
      state.data = payload;
      state.isLoading = false;
    });
    builder.addCase(actionGetBanners.rejected, (state, action) => {
      state.error = action.error;
      state.isLoading = false;
    });
  },
  initialState,
  name: 'banners',
  reducers: {
    actionSetDiscountBanner: (state, { payload }: PayloadAction<TBanner | null>) => {
      state.discountBanner = payload;
    },
    actionSetIsDiscountVoucherShown: (state, { payload }: PayloadAction<boolean>) => {
      state.isDiscountVoucherShown = payload;
    },
  },
});

export const { actionSetDiscountBanner, actionSetIsDiscountVoucherShown } = bannersSlice.actions;

export default bannersSlice.reducer;
